import { useEffect, useRef, useState } from "react";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { useTranslation } from "react-i18next";

const YearRangePicker = ({
	minYear,
	maxYear,
	startYear,
	endYear,
	disable,
	style,
	classNames = "",
	spacer = "/",
	startText = "from",
	endText = "to",
	selectedColor = "#63C29A",
	onSelect,
}) => {
	const { t } = useTranslation();
	const [years, setYears] = useState([]);
	const [selectedStartYear, setSelectedStartYear] = useState(0);
	const [selectedEndYear, setSelectedEndYear] = useState(0);
	const [optionsVisible, setOptionsVisible] = useState(false);
	const containerRef = useRef(null);

	const setInitialYear = (minYear = 0, maxYear = 0) => {
		const years = [];
		for (let year = minYear; year <= maxYear; year++) years.push(year);
		setYears(years);
	};

	const handleClickOutside = (e) => {
		if (containerRef.current && !containerRef.current.contains(e.target))
			setOptionsVisible(false);
	};

	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		setInitialYear(minYear, maxYear);
	}, [minYear, maxYear]);

	useEffect(() => {
		setSelectedStartYear(startYear ? startYear : 0);
		setSelectedEndYear(endYear ? endYear : 0);
	}, [startYear, endYear]);

	const handleSelect = (start, end) => {
		if (start !== 0 && end !== 0) {
			setOptionsVisible(false);
			if (onSelect) onSelect(start, end);
		}
	};

	const listItemStyle = (selected, disabled) => ({
		padding: "5px",
		marginBottom: "5px",
		background: selected ? selectedColor : "#fff",
		color: selected ? "#fff" : disabled ? "#aaa" : "#000",
		pointerEvents: disabled ? "none" : "auto",
		cursor: disabled ? "auto" : "pointer",
	});

	const renderStartYearPicker = () => {
		return (
			<div style={{ textAlign: "center", flexGrow: 1 }}>
				<span>{t(startText)}</span>
				<ul style={{ listStyle: "none", padding: "0 10px" }}>
					{years.map((year) => (
						<li
							key={year}
							style={listItemStyle(
								selectedStartYear === year,
								selectedEndYear !== 0 && selectedEndYear < year
							)}
							onClick={() => {
								handleSelect(year, selectedEndYear);
								setSelectedStartYear(year);
							}}
						>
							{year}
						</li>
					))}
				</ul>
			</div>
		);
	};

	const renderEndYearPicker = () => {
		return (
			<div style={{ textAlign: "center", flexGrow: 1 }}>
				<span>{t(endText)}</span>
				<ul style={{ listStyle: "none", padding: "0 10px" }}>
					{years.map((year) => (
						<li
							key={year}
							style={listItemStyle(
								selectedEndYear === year,
								selectedStartYear !== 0 && selectedStartYear > year
							)}
							onClick={() => {
								handleSelect(selectedStartYear, year);
								setSelectedEndYear(year);
							}}
						>
							{year}
						</li>
					))}
				</ul>
			</div>
		);
	};

	const renderOptions = () => {
		return (
			<div
				style={{
					position: "absolute",
					width: "100%",
					height: "auto",
					paddingTop: "10px",
					display: "flex",
					justifyContent: "space-between",
					alignItems: "flex-start",
					background: "#fff",
					boxShadow:
						"0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12)",
					borderTop: "none",
					marginLeft: "-1px",
					marginTop: "1px",
					overflowY: "auto",
					zIndex: 10,
					borderRadius: "4px",
				}}
				className="yrp-options-container"
			>
				{renderStartYearPicker()}
				{renderEndYearPicker()}
			</div>
		);
	};

	const renderSelectedYears = () => {
		return (
			<div
				style={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					padding: "6px 6px 6px 14px",
				}}
				className="yrp-picker-box"
				onClick={() => setOptionsVisible(true)}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						flexGrow: 1,
						color: "#000",
					}}
					className="yrp-picker-text"
				>
					<span>
						{selectedStartYear === 0 ? t(startText) : selectedStartYear}
					</span>
					<span
						style={{ marginLeft: "5px", marginRight: "5px" }}
						className="yrp-spacer"
					>
						{spacer}
					</span>
					<span>{selectedEndYear === 0 ? t(endText) : selectedEndYear}</span>
				</div>

				<button
					style={{
						border: "none",
						background: "none",
						marginLeft: "10px",
						paddingRight: 0,
					}}
					className="yrp-arrow-button"
					type="button"
				>
					<ArrowDropDownIcon
						sx={{
							color: "rgba(0, 0, 0, 0.54)",
							transform: optionsVisible ? "rotate(180deg)" : "",
						}}
					/>
				</button>
			</div>
		);
	};

	return (
		<div
			style={{
				...style,
				border: "1px solid rgba(0, 0, 0, 0.23)",
				borderRadius: "4px",
				minWidth: "200px",
				cursor: "pointer",
				display: "inline-block",
				position: "relative",
				...(disable === true
					? {
							pointerEvents: "none",
							color: "#aaa",
							background: "#eee",
							cursor: "auto",
					  }
					: {}),
			}}
			className={`yrp-container ${classNames}`}
			ref={containerRef}
		>
			{renderSelectedYears()}
			{optionsVisible && renderOptions()}
		</div>
	);
};

export default YearRangePicker;
